import { debounce } from "lodash";

import type { Primitive } from "@similarweb/sw-track";

import { TRACK_DEBOUNCE_TIMEOUT } from "app/pages/sales-intelligence/constants/timeouts";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import createTrackingEventName from "app/pages/sales-intelligence/utils/testing/createTrackingEventName";
import { getTrackingNameByExportNumber } from "app/pages/sales-intelligence/utils/utils";
import { allTrackers } from "app/services/track/track";

import type { SelectionDropdownItem } from "../../../table-actions-panel/models/selection-dropdown-item/types";
import type { ContactSearchEntry, ExportDestination } from "../../types/common";
import { ContactReportReason } from "../../types/consts";
import type { ContactIdCompanyPair } from "../../types/dto";

import type { TrackingServiceDeps } from "./types";

export function createContactsSearchTrackingService({ track }: TrackingServiceDeps) {
    function trackWithTableCategory(action: string, event: string) {
        track("table", action, event);
    }

    function trackWithActionsPanelCategory(action: string, event: string) {
        track("multi select actions", action, event);
    }

    function trackWithContactsCategory(action: string, event: string) {
        track("contacts", action, event);
    }

    function trackWithContactsRequestCategory(action: string, event: string) {
        track("request access", action, event);
    }

    function getAvailableDetailsFromEntry(entry: ContactSearchEntry) {
        const details: string[] = [];

        if (entry.hasValidEmails) {
            details.push("email");
        }

        if (entry.hasMobilePhoneNumbers) {
            details.push("mobile phone");
        }

        if (entry.hasDirectPhoneNumbers) {
            details.push("direct phone");
        }

        return details;
    }

    function trackAccountSearchImmediate(crm: Integration) {
        track(`${crm} integration`, "search account", "export/review");
    }

    function trackContactsOptions(action: string, eventName: string, customData?: Record<string, Primitive>) {
        track("contacts options", action, eventName, customData);
    }

    function trackContactsOptionsClick(eventName: string) {
        trackContactsOptions("click", eventName);
    }

    function trackReportIssue(eventSubName?: string, customData?: Record<string, Primitive>) {
        trackContactsOptions("click", eventSubName ? `report issue/${eventSubName}` : "report issue", customData);
    }

    function getProvider(contactId: string): string {
        if (contactId.endsWith("_")) {
            return "Z";
        }
        if (contactId.startsWith("_")) {
            return "R";
        }
        return "ZR";
    }

    function trackRevealAndExport(event: string) {
        trackWithActionsPanelCategory("reveal and export", event);
    }

    return {
        trackFeatureAccessRequested(role = "") {
            trackWithTableCategory("request access", `contacts results/${role}`);
        },
        trackApproveTerms() {
            trackWithContactsRequestCategory("approve terms", "request contacts t&c/approve");
        },
        trackContactEntryExpanded(entry: ContactSearchEntry) {
            trackWithTableCategory("expand contact", `contacts results/${getAvailableDetailsFromEntry(entry)}`);
        },
        trackContactEntryCollapsed(entry: ContactSearchEntry) {
            trackWithTableCategory("collapse contact", `contacts results/${getAvailableDetailsFromEntry(entry)}`);
        },
        trackContactEntryRevealClicked(entry: ContactSearchEntry) {
            trackWithTableCategory("reveal", `contacts results/${getAvailableDetailsFromEntry(entry)}`);
        },
        trackContactEntrySingleRevealSuccess() {
            trackWithContactsCategory("revealed successfully", "single reveal/1/1");
        },
        trackContactEntrySingleRevealFailure() {
            trackWithContactsCategory("revealed failure", "single reveal/1/1");
        },
        trackContactsBulkRevealSuccess(revealedQuantity: number, totalQuantity: number) {
            trackWithContactsCategory("revealed successfully", `bulk reveal/${revealedQuantity}/${totalQuantity}`);
        },
        trackContactsBulkRevealFailure(failedQuantity: number, totalQuantity: number) {
            trackWithContactsCategory("revealed failure", `bulk reveal/${failedQuantity}/${totalQuantity}`);
        },
        trackRevealOptionsClicked() {
            trackWithActionsPanelCategory(`reveal`, "reveal options//");
        },
        trackTrialRevealOptionsClicked(url: string) {
            const eventName = createTrackingEventName(url);
            track("contact reveal hook", "click", eventName);
        },
        trackTrialExportOptionsClicked(url: string) {
            const eventName = createTrackingEventName(url);
            track("contact export hook", "click", eventName);
        },
        trackRevealSubmitted(selectionOption: SelectionDropdownItem, remainingQuota: number) {
            const value =
                selectionOption.id === "manual-selection" ? "manual selection" : `top ${selectionOption.value}`;

            trackWithActionsPanelCategory(
                `reveal ${value}`,
                `reveal options/${remainingQuota}/${selectionOption.value}`,
            );
        },
        trackSelectExportDestination(exportDestination: ExportDestination) {
            trackWithActionsPanelCategory(
                "select export location",
                `export options/export contact/${exportDestination}`,
            );
        },
        trackCloseExportPanel(exportDestination: ExportDestination) {
            trackWithActionsPanelCategory("close banner", `contacts results/export contact/${exportDestination}`);
        },
        trackClickOnExport(exportDestination: ExportDestination, contactsCount: number, quota: number) {
            trackWithActionsPanelCategory(
                `export to + ${exportDestination} + ${getTrackingNameByExportNumber(quota, contactsCount)}`,
                `contacts results/${quota}/${contactsCount}`,
            );
        },
        trackCancelExportWhileLoading(crm: Integration) {
            trackWithActionsPanelCategory("cancel export", `contacts results/export contact/${crm}`);
        },
        trackCancelExportWhileReviewing(crm: Integration) {
            track(`${crm} integration`, "cancel export", "export/review/cancel export");
        },
        trackRemoveContactFromExport(crm: Integration) {
            track(`${crm} integration`, "match account", "export/review/remove contact");
        },
        trackContactsExportToCrm(crm: Integration, selected: number, exported: number) {
            const status = exported ? "success" : "fail";
            trackWithActionsPanelCategory(`export contacts to ${crm}`, `${status}/${selected}/${exported}`);
        },
        trackAssignAccountToContact(crm: Integration, isNewAccount: boolean) {
            const accountType = isNewAccount ? "new account" : "existing account";
            track(`${crm} integration`, "match account", `export/review/${accountType}`);
        },
        trackAccountSearch: debounce(trackAccountSearchImmediate, TRACK_DEBOUNCE_TIMEOUT),
        trackAccountSearchImmediate,
        trackApproveExportToCrm(crm: Integration, contactsCount: number) {
            track(`${crm} integration`, "approve export", `export/review/${contactsCount}`);
        },
        trackContactsExport(topNOrPairs: number | readonly ContactIdCompanyPair[], exportTo: string, quota: number) {
            const [value, count] =
                typeof topNOrPairs === "number"
                    ? [`top ${topNOrPairs}`, topNOrPairs]
                    : ["manual selection", topNOrPairs.length];

            track("multi select actions", `export to ${exportTo} ${value}`, `contacts results/${quota}/${count}`);
        },
        trackLinkedInLinkClick(title: string, isRevealed: boolean) {
            trackWithTableCategory(
                `open linkedin`,
                `contacts results/${title}/${isRevealed ? "revealed" : "not revealed"}`,
            );
        },
        trackRevealContactClick() {
            trackContactsOptions("click reveal", "reveal contact");
        },
        trackContactDetailsCopy() {
            trackContactsOptions("copy", "copy contact details");
        },
        trackPrepareForMeetingClick() {
            trackContactsOptionsClick("prepare for a meeting");
        },
        trackCraftEmailClick() {
            trackContactsOptionsClick("craft an email");
        },
        trackLinkedInClick() {
            trackContactsOptionsClick("open linkedin");
        },
        trackSendWhatsappClick() {
            trackContactsOptionsClick("send whatsapp");
        },
        trackExportContactClick() {
            trackContactsOptionsClick("export contact");
        },
        trackPageNavigation(direction: string) {
            const event = createTrackingEventName("page navigation", direction);

            trackContactsOptionsClick(event);
        },
        trackSortColumn(columnName: string) {
            const event = createTrackingEventName("sort column", columnName);

            trackContactsOptionsClick(event);
        },
        trackRevealedContactsOnlyToggle(isSelected: boolean) {
            const event = createTrackingEventName(
                "revealed contacts only toggle",
                isSelected ? "checked" : "unchecked",
            );

            trackContactsOptionsClick(event);
        },
        trackReportIssueOpenModal() {
            trackReportIssue("open modal");
        },
        trackReportIssueSelectCheckbox(value: boolean, key: string, contactId: string) {
            trackReportIssue(`selected checkbox/${value ? "checked" : "unchecked"}`, {
                reported_item: key,
                provider: getProvider(contactId),
            });
        },
        trackReportIssueOther(notesInfo: string) {
            trackReportIssue(`other/${notesInfo}`);
        },
        trackReportIssueCancel() {
            trackReportIssue("close modal");
        },
        trackReportIssueSubmit(selectedReasons: string[], contact: ContactSearchEntry, additionalInfo?: string) {
            const reportedValues: string[] = selectedReasons.map((reason) => {
                if (reason === ContactReportReason.EmailIncorrect) {
                    return contact.privateData?.emails?.join(";") ?? "";
                }
                if (reason === ContactReportReason.MobileIncorrect) {
                    return contact.privateData?.phoneNumbers?.find((p) => p.internalType === "mobile")?.number ?? "";
                }
                if (reason === ContactReportReason.DirectIncorrect) {
                    return contact.privateData?.phoneNumbers?.find((p) => p.internalType === "direct")?.number ?? "";
                }
                if (reason === ContactReportReason.CompanyIncorrect) {
                    return contact.companyName ?? "";
                }
                if (reason === ContactReportReason.RoleIncorrect) {
                    return contact.title ?? "";
                }
                if (reason === ContactReportReason.Other) {
                    return additionalInfo ?? "";
                }
                return "";
            });

            trackContactsOptions("submit-ok", "report issue", {
                provider: getProvider(contact.id),
                reported_item: reportedValues.join(","),
                reported_type: selectedReasons.join(","),
            });
        },

        trackSubmitRevealAndExport(
            revealStatus: "success" | "partial" | "failure",
            exportStatus: "success" | "partial" | "failure",
        ) {
            const event = createTrackingEventName("continue", `reveal ${revealStatus}`, `export ${exportStatus}`);
            trackRevealAndExport(event);
        },

        trackCancelRevealAndExport() {
            trackRevealAndExport("cancel");
        },

        trackContactUsRevealAndExport() {
            trackRevealAndExport("contact us");
        },
    };
}

export default createContactsSearchTrackingService({
    track: allTrackers.dispatchTrackEvent.bind(allTrackers),
});
